import { Component, EventEmitter, inject, Inject, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  ApprovalTypes,
  GeneralLedgerDownloadTypes,
  GeneralLedgerExportFrequencies,
  GeneralLedgerExportTypes,
  GLConfiguration,
  QboCredentialRequirements
} from '../../../../../models/general-ledger';
import { Select, Store } from '@ngxs/store';
import { ClientSelectionState } from '../../../../../state/ClientSelectionState/ClientSelection.state';
import { Observable, of } from 'rxjs';
import { ClientSetup } from '../../../../../models/client';
import { InstanceTypes } from '../../../../../models/instance-types';
import { GeneralLedgerService } from '../../../../../services/general-ledger.service';
import { ModalService } from '../../../../../services/modal.service';
import { concatMap, map, mergeMap } from 'rxjs/operators';
import { Navigate } from '@ngxs/router-plugin';
import { ModalStates } from '../../../../../models/modal';
import {
  dateRangeComponentValidator,
  dateRangeRequired
} from '../../../../../utils/dateRangeValidator';
import {
  GeneralLedgerState,
  GlDoExport,
  GlLoadConfigs,
  GlSelectedConfigChanged,
  GlTabChanged
} from '../../../../../state/GeneralLedgerState/GeneralLedger.state';
import { GlUniqueNameValidator } from './GlUniqueNameValidator';
import { ToastService } from '../../../../../services/toast.service';

@Component({
  selector: 'lib-export-files-primary',
  templateUrl: './export-files-primary.component.html',
  styleUrls: ['./export-files-primary.component.css'],
  standalone: false
})
export class ExportFilesPrimaryComponent implements OnInit {
  @Output()
  showMappings: EventEmitter<any> = new EventEmitter<any>();
  @Output()
  showSettings: EventEmitter<any> = new EventEmitter<any>();
  @Output()
  editMappings: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Input() inUpdateViewState: boolean = false;

  @Select(GeneralLedgerState.getSelectedConfig)
  selected: Observable<number | null>;
  @Select(GeneralLedgerState.getSelectedConfigObject)
  selectedObj: Observable<GLConfiguration>;
  @Select(GeneralLedgerState.hasOneAutomatic)
  hasOneAutomatic: Observable<boolean>;

  @Select(GeneralLedgerState.getBalance)
  balance: Observable<number | null>;

  form: FormGroup;
  exportTypes = GeneralLedgerExportTypes;
  frequencies = GeneralLedgerExportFrequencies;
  balanceTypes = GeneralLedgerDownloadTypes;
  approvalTypes = ApprovalTypes;
  connectedToQbo = false;
  @Select(GeneralLedgerState.getConfigs)
  glConfigs: Observable<GLConfiguration[]>;
  disabled: boolean = false;

  @Select(ClientSelectionState.clientSetup)
  clientSetup: Observable<ClientSetup>;
  @Select(ClientSelectionState.clientCode)
  clientCode: Observable<string>;

  public nameChanged = false;
  public initComplete = false;

  get isClientPortal() {
    return this.env.instanceType === InstanceTypes.ClientPortal;
  }

  private readonly toastService = inject(ToastService);

  constructor(
    private fb: FormBuilder,
    private store: Store,
    private service: GeneralLedgerService,
    private modalService: ModalService,
    private glNameValidator: GlUniqueNameValidator,
    @Inject('env') private env: any
  ) {}

  ngOnInit() {
    this.store.selectOnce(GeneralLedgerState.getConfigs).subscribe(configs => {
      if (configs == null || configs.length == 0) {
        this.store.dispatch(new GlLoadConfigs());
      }
    });
    if (this.inUpdateViewState) {
      this.store.selectOnce(GeneralLedgerState.getSelectedConfigObject).subscribe(existing => {
        if (existing != null) {
          this.initForm(existing);
        }
      });
    } else {
      this.initForm();
    }
  }

  public canAddAutomatic(current: GLConfiguration | null) {
    return this.store.selectOnce(GeneralLedgerState.getConfigs).pipe(
      map(items => {
        const auto = items.filter(x => x.SubmissionType == ApprovalTypes.Automatic);
        if (auto.some(x => x.Id == current?.Id)) {
          return true;
        }
        if (auto == null || auto.length == 0) {
          return true;
        } else if (auto.length == 1 && auto[0].ExportFileType != GeneralLedgerExportTypes.QBO) {
          return false;
        } else {
          if (auto.length == 2) {
            //We already have two, so we can't add a new one.
            //We do still need to show it if the one we are editing is one of the ones already in the list.
            if (current == null) return false;
            const currentIsAuto = auto.some(x => x.Id === current.Id);
            return currentIsAuto;
          }
          const currentExportType = this.form.get('exportTo').value;
          const currentBalanceType = this.form.get('balanceType').value;

          return (
            currentExportType == GeneralLedgerExportTypes.QBO && currentBalanceType != auto[0].Type
          );
        }
      })
    );
  }

  public changeState(id: number, updateState: boolean = true) {
    this.form.reset();

    if (updateState) {
      this.store.dispatch(new GlSelectedConfigChanged(id)).subscribe(_ => {
        this.setExistingConfigValues();
      });
    } else {
      this.setExistingConfigValues();
    }

    if (id > 0 && !this.inUpdateViewState) {
      this.setExistingConfigValues();
      this.form.get('exportTo').disable();
      this.form.get('frequency').disable();
      this.form.get('submitWithoutApproval').disable();
      this.form.get('useClasses').disable();
      this.form.get('balanceType').disable();
      this.form.get('name').disable();
    } else {
      this.form.get('exportTo').enable();
      this.form.get('frequency').enable();
      this.form.get('submitWithoutApproval').enable();
      this.form.get('useClasses').enable();
      this.form.get('balanceType').enable();
      this.form.get('name').enable();
    }
  }

  public navigateToPayrollHub() {
    this.store.selectOnce(ClientSelectionState.clientCode).subscribe(code => {
      this.store.dispatch(new Navigate([`/client/${code}/payroll-landing`]));
    });
  }

  // task #25894. when updating a GL setup, pop nav confirmation modal if unsaved changes exist
  public onDeactivate() {
    if (!this.inUpdateViewState) {
      return of(true);
    }

    return this.store.selectOnce(GeneralLedgerState.getConfigs).pipe(
      concatMap(configs => {
        return this.store.selectOnce(GeneralLedgerState.getSelectedConfig).pipe(
          map(configId => {
            let config = { ...configs.find(x => x.Id == configId) };

            if (!config) {
              throw new Error('unable to find GL config record');
            }
            const formModel = this.form.value;

            return (
              config.ExportFileType == formModel.exportTo &&
              config.TimeFrame == formModel.frequency &&
              config.SubmissionType == formModel.submitWithoutApproval &&
              config.UseClasses == (formModel.useClasses ?? false)
            );
          })
        );
      })
    );
  }

  public setExistingConfigValues() {
    this.store.selectOnce(GeneralLedgerState.getSelectedConfigObject).subscribe(config => {
      if (config) {
        this.form.get('exportTo').setValue(config.ExportFileType);
        this.form.get('frequency').setValue(config.TimeFrame);
        this.form.get('submitWithoutApproval').setValue(config.SubmissionType);
        this.form.get('useClasses').setValue(config.UseClasses);
        this.form.get('balanceType').setValue(config.Type);
        this.form.get('timeframe').setValue({ start: config.FromDate, end: config.ToDate });
        this.form.get('name').setValue(config.Name);
        this.connectedToQbo = config.ConnectedToQBO;
      }
    });
  }

  public startQboConnection() {
    const popupHeight = 400;
    const popupWidth = 400;
    const left = (screen.width - popupWidth) / 2;
    const top = (screen.height - popupHeight) / 2;
    const qboUrl =
      'https://appcenter.intuit.com/connect/oauth2?client_id=' +
      this.env.qboAppId +
      '&scope=com.intuit.quickbooks.accounting%20openid%20email&redirect_uri=' +
      this.env.qboRedirectUri +
      '&response_type=code&claims=%7B%22id_token%22%3A%7B%22realmId%22%3Anull%7D%7D&state=138r5719ru3e1';
    const popupInjection = `window.onload = function()  {
      window.location.href = '${qboUrl}';
    }`;
    const popupWindow = window.open(
      '/qbocallback',
      'Connect to Quickbooks Online',
      `height=${popupHeight},width=${popupWidth},left=${left},top=${top}`
    );
    const script = popupWindow.document.createElement('script');
    let userInterruptedSignOn: boolean = false;
    script.innerHTML = popupInjection;
    popupWindow.document.body.appendChild(script);
    //This safety valve is to prevent infinite recursion if something goes wrong with the QBO connection.
    //every 200 attempts equates to 1 minute. Since we want to make sure that we aren't interrupting them.
    // we're giving 5 minutes worth of attempts.
    const safetyValvelimit = 1000;
    let safetyValveCurrent = 0;
    const poller = () => {
      if (safetyValveCurrent < safetyValvelimit) {
        safetyValveCurrent++;
        setTimeout(() => {
          try {
            if (popupWindow.location.href.includes('qboredirectfinished')) {
              const url = popupWindow.location.href;
              popupWindow.close();
              const splitFromQueryString = url.split('?');
              const splitQueryString = splitFromQueryString[1].split('&');
              const final: QboCredentialRequirements = {
                Code: '',
                RealmId: '',
                State: ''
              };
              splitQueryString.forEach(qsItem => {
                const val = qsItem.split('=');
                switch (val[0]) {
                  case 'code':
                    final.Code = val[1];
                    break;
                  case 'realmId':
                    final.RealmId = val[1];
                    break;
                  case 'state':
                    final.State = val[1];
                }
              });
              this.store
                .selectOnce(ClientSelectionState.clientCode)
                .pipe(
                  mergeMap(code => {
                    return this.service.completeQboConnection(code, final);
                  })
                )
                .subscribe(() => {
                  this.connectedToQbo = true;
                });
            } else {
              userInterruptedSignOn = true;
              poller();
            }
          } catch (e) {
            poller();
          }
        }, 300);
      } else {
        popupWindow.close();
        if (!userInterruptedSignOn) {
          this.modalService.showAlertModal(
            'Unable to connect to Quickbooks Online. Please try again. If this message keeps appearing, please contact your Payroll Specialist.',
            ModalStates.Error
          );
        }
      }
    };
    poller();
  }

  public onEditMapping() {
    this.editMappings.emit(this.connectedToQbo);
  }
  public onShowMappings() {
    this.store.selectOnce(GeneralLedgerState.getSelectedConfigObject).subscribe(config => {
      this.showMappings.emit(config);
    });
  }

  public exportFile() {
    this.store
      .selectOnce(GeneralLedgerState.getSelectedConfig)
      .pipe(
        mergeMap(configId => {
          return this.store.dispatch(new GlDoExport(configId));
        })
      )
      .subscribe(_ => {
        this.store.dispatch(new GlTabChanged(2));
      });
  }
  public cancel() {
    if (this.form.dirty) {
      this.modalService.confirmModalCallback.subscribe(val => {
        if (val) {
          this.showSettings.emit();
        }
      });
      this.modalService.showConfirmModal(
        'Are you sure you want to leave this page? Changes you made will not be saved.',
        'Cancel',
        'Leave Page',
        'Unsaved Changes'
      );
    } else {
      this.showSettings.emit();
    }
  }

  public submit() {
    this.store.selectOnce(ClientSelectionState.clientCode).subscribe(code => {
      const formModel = this.form.value;
      const type = formModel.balanceType ?? GeneralLedgerDownloadTypes.Totals;
      //We only want to use classes if the form is opted in, the export type is QBO, and the DL type is Totals.
      let useClasses =
        formModel.useClasses &&
        formModel.exportTo == GeneralLedgerExportTypes.QBO &&
        type == GeneralLedgerDownloadTypes.Totals;
      if (this.inUpdateViewState) {
        // updating existing config
        this.store.selectOnce(GeneralLedgerState.getSelectedConfigObject).subscribe(config => {
          if (!config) {
            throw new Error('unable to find GL config record');
          }

          const clone: GLConfiguration = structuredClone(config);
          clone.ExportFileType = formModel.exportTo;
          clone.TimeFrame = formModel.frequency;
          clone.SubmissionType = formModel.submitWithoutApproval;
          clone.Type = type;
          clone.UseClasses = useClasses ?? false;
          clone.FromDate = formModel.timeframe?.start;
          clone.ToDate = formModel.timeframe?.end;
          clone.Name = formModel.name;
          clone.ConnectedToQBO = this.connectedToQbo;
          this.service.updateGlConfiguration(code, clone).subscribe(() => {
            this.store.dispatch(new GlLoadConfigs()).subscribe(_ => {
              this.store.dispatch(new GlSelectedConfigChanged(clone.Id)).subscribe(__ => {
                this.showSettings.emit();
                this.toastService.show(`${clone.Name} successfully updated`);
              });
            });
          });
        });
      } else {
        // create new config
        const model: GLConfiguration = {
          Obsolete: false,
          ConfigId: null,
          Name: this.form.get('name').value,
          ExportFileType: formModel.exportTo,
          TimeFrame: formModel.frequency,
          SubmissionType: formModel.submitWithoutApproval,
          Type: type,
          UseClasses: useClasses ?? false,
          GLAccount: '',
          Offset: '',
          ClientCode: code,
          ColOrder: [],
          Id: null,
          ConnectedToQBO: this.connectedToQbo,
          FromDate: formModel.timeframe?.start,
          ToDate: formModel.timeframe?.end,
          CreateNewClasses: false,
          Username: null
        };
        this.service.createGLConfiguration(code, model).subscribe(result => {
          this.store.dispatch(new GlLoadConfigs()).subscribe(_ => {
            this.store.dispatch(new GlSelectedConfigChanged(result?.Id)).subscribe(__ => {
              //Should redirect to mapping screens after this point.
              this.showMappings.emit(result);
            });
          });
        });
      }
    });
  }

  private initForm(initialModel: GLConfiguration | null = null) {
    console.log(initialModel);
    this.form = this.fb.group({
      exportTo: this.fb.control(initialModel?.ExportFileType, Validators.required),
      frequency: this.fb.control(initialModel?.TimeFrame, Validators.required),
      submitWithoutApproval: this.fb.control(initialModel?.SubmissionType, Validators.required),
      name: this.fb.control(
        initialModel?.Name,
        [Validators.required, Validators.minLength(3)],
        [this.glNameValidator.validate.bind(this.glNameValidator)]
      ),
      balanceType: this.fb.control(initialModel?.Type),
      useClasses: this.fb.control(initialModel?.UseClasses),
      timeframe: this.fb.control({ start: initialModel?.FromDate, end: initialModel?.ToDate })
    });

    this.form.valueChanges.subscribe(val => {
      if (
        val.exportTo == GeneralLedgerExportTypes.QBO &&
        val.balanceType == GeneralLedgerDownloadTypes.Totals
      ) {
        this.form.get('useClasses').setValidators(Validators.required);
      } else {
        this.form.get('useClasses').setValidators([]);
      }
      if (val.frequency == GeneralLedgerExportFrequencies.DateRange && !this.inUpdateViewState) {
        this.form.get('timeframe').setValidators([dateRangeRequired, dateRangeComponentValidator]);
      } else {
        this.form.get('timeframe').setValidators([]);
      }
    });

    this.form.get('name').valueChanges.subscribe(val => {
      if (this.initComplete) {
        this.nameChanged = true;
      }
    });

    this.form.get('frequency').valueChanges.subscribe((val: GeneralLedgerExportFrequencies) => {
      if (val == null || this.form.get('name').dirty || this.inUpdateViewState) {
        return;
      }
      let name = 'GL_';
      switch (val) {
        case GeneralLedgerExportFrequencies.Payroll:
          name = `${name}PayPeriod`;
          break;
        case GeneralLedgerExportFrequencies.MTD:
          name = `${name}Monthly`;
          break;
        case GeneralLedgerExportFrequencies.QTD:
          name = `${name}Quarterly`;
          break;
        case GeneralLedgerExportFrequencies.YTD:
          name = `${name}Annually`;
          break;
        case GeneralLedgerExportFrequencies.DateRange:
          name = `${name}Custom`;
          break;
      }
      this.store.selectOnce(GeneralLedgerState.getFormattedName(name)).subscribe(name => {
        this.form.get('name').setValue(name, { emitEvent: false });
      });
    });

    this.store.selectOnce(GeneralLedgerState.getSelectedConfig).subscribe(configId => {
      if (configId != null) {
        this.changeState(configId, false);
      }
    });
    this.initComplete = true;
  }
}
